import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

/** Navigation state for a forward navigation that means "go back a step", so it slides as Back. */
export const backStepState = { stepDirection: 'back' } as const;

const isBackStepState = (state: unknown) =>
  typeof state === 'object' &&
  state !== null &&
  'stepDirection' in state &&
  state.stepDirection === backStepState.stepDirection;

/** Reads the motion tokens from index.css so JS and CSS motion stay on one scale. */
const readMotion = () => {
  const styles = getComputedStyle(document.documentElement);
  const token = (name: string) => styles.getPropertyValue(name).trim();
  const number = (name: string, fallback: number) => {
    const raw = token(name);
    const value = Number.parseFloat(raw);

    if (!Number.isFinite(value)) {
      return fallback;
    }

    return raw.endsWith('ms') || !raw.endsWith('s') ? value : value * 1000;
  };

  return {
    // Emil Kowalski's strong ease-out for the per-sign-in reveals; the page slide is surface motion.
    easeOut: token('--ease-out-strong') || 'cubic-bezier(0.23, 1, 0.32, 1)',
    smoothOut: token('--ease-smooth-out') || 'cubic-bezier(0.22, 1, 0.36, 1)',
    // Texts reveal (transitions.dev), trimmed from the recipe's 500ms: this plays on every sign-in.
    textsReveal: {
      duration: number('--duration-fast', 250),
      distance: number('--distance-medium', 12),
      blur: number('--blur-medium', 3),
      stagger: number('--duration-stagger', 40),
      maxSteps: 5,
    },
    pageSlide: {
      duration: number('--duration-fast', 250),
      distance: number('--distance-base', 8),
      blur: number('--blur-medium', 3),
    },
    revealInPlace: { duration: number('--duration-fast', 250), blur: number('--blur-small', 2) },
    // Reduced motion keeps a short fade so the change still reads; only movement is dropped.
    reducedFade: { duration: number('--duration-quick', 150) },
  };
};

// eslint-disable-next-line @typescript-eslint/ban-types -- React 19's useRef(null) yields RefObject<T | null>
type ElementRef = RefObject<HTMLElement | null>;

type MotionMode = 'full' | 'reduced' | 'none';

const getMotionMode = (element: HTMLElement): MotionMode => {
  if (typeof element.animate !== 'function') {
    return 'none';
  }

  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'reduced'
    : 'full';
};

/**
 * The wrapper persists across steps, so a quick Back/Forward lands mid-animation: continue from
 * the opacity on screen rather than flashing back to transparent.
 */
const takeOverOpacity = (element: HTMLElement) => {
  const opacity = Number.parseFloat(getComputedStyle(element).opacity);

  for (const animation of element.getAnimations()) {
    animation.cancel();
  }

  return Number.isFinite(opacity) && opacity < 1 ? opacity : 0;
};

const fadeIn = (element: HTMLElement) =>
  element.animate([{ opacity: takeOverOpacity(element) }, { opacity: 1 }], {
    duration: readMotion().reducedFade.duration,
    easing: 'ease',
  });

/** The page's visual chunks (logo, title block, form, footer): skip single-child wrappers. */
const getChunks = (content: HTMLElement): HTMLElement[] => {
  // eslint-disable-next-line @silverhand/fp/no-let -- walking down a wrapper chain
  let node: Element = content;

  while (node.children.length === 1 && node.firstElementChild) {
    // eslint-disable-next-line @silverhand/fp/no-mutation
    node = node.firstElementChild;
  }

  return [...node.children].filter(
    (child): child is HTMLElement => child instanceof HTMLElement && child.offsetHeight > 0
  );
};

const revealChunks = (content: HTMLElement) => {
  const mode = getMotionMode(content);

  if (mode === 'none') {
    return;
  }

  if (mode === 'reduced') {
    fadeIn(content);
    return;
  }

  const { easeOut, textsReveal } = readMotion();
  const { duration, distance, blur, stagger, maxSteps } = textsReveal;

  for (const [index, chunk] of getChunks(content).entries()) {
    chunk.animate(
      [
        { opacity: 0, transform: `translateY(${distance}px)`, filter: `blur(${blur}px)` },
        { opacity: 1, transform: 'none', filter: 'none' },
      ],
      // `backwards` holds later chunks invisible through their delay instead of flashing.
      { duration, easing: easeOut, delay: Math.min(index, maxSteps) * stagger, fill: 'backwards' }
    );
  }
};

/** Skeleton reveal (transitions.dev): content that replaces a placeholder in place. */
const revealInPlace = (content: HTMLElement) => {
  const mode = getMotionMode(content);

  if (mode === 'none') {
    return;
  }

  if (mode === 'reduced') {
    fadeIn(content);
    return;
  }

  const { easeOut, revealInPlace: reveal } = readMotion();

  content.animate(
    [
      { opacity: takeOverOpacity(content), filter: `blur(${reveal.blur}px)` },
      { opacity: 1, filter: 'none' },
    ],
    { duration: reveal.duration, easing: easeOut }
  );
};

/** Forward steps enter from the trailing side, Back from the leading side (mirrored in RTL). */
const slideInPage = (content: HTMLElement, isBack: boolean) => {
  const mode = getMotionMode(content);

  if (mode === 'none') {
    return;
  }

  if (mode === 'reduced') {
    fadeIn(content);
    return;
  }

  const { smoothOut, pageSlide } = readMotion();
  const trailingSign = getComputedStyle(content).direction === 'rtl' ? -1 : 1;
  const fromX = pageSlide.distance * trailingSign * (isBack ? -1 : 1);

  content.animate(
    [
      {
        opacity: takeOverOpacity(content),
        transform: `translateX(${fromX}px)`,
        filter: `blur(${pageSlide.blur}px)`,
      },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
    { duration: pageSlide.duration, easing: smoothOut }
  );
};

/** React Router's position in the history stack, kept in `history.state.idx`. */
const readHistoryIndex = (state: unknown): number | undefined =>
  typeof state === 'object' && state !== null && 'idx' in state && typeof state.idx === 'number'
    ? state.idx
    : undefined;

const getHistoryIndex = () => readHistoryIndex(window.history.state);

/**
 * Animates each auth step in without remounting the route tree (a keyed remount would reset
 * form state and re-run guards).
 */
const useStepTransition = (contentRef: ElementRef) => {
  const { pathname, state } = useLocation();
  const navigationType = useNavigationType();
  const previousPathname = useRef(pathname);
  const isBack = useRef(false);
  const previousHistoryIndex = useRef(getHistoryIndex());
  // Page roots already animated, so a lazy page resolving after its route change and the route
  // change itself never both animate the same screen.
  const animatedRoots = useRef(new WeakSet<Element>());
  const hasRevealed = useRef(false);

  // First paint reveals; later root swaps without a route change (a lazy page resolving, data
  // replacing a skeleton) cross-blur in place, since the user did not move anywhere.
  useEffect(() => {
    const content = contentRef.current;

    if (!content) {
      return;
    }

    const animateNewRoot = () => {
      const root = content.firstElementChild;

      if (!root || animatedRoots.current.has(root)) {
        return;
      }

      animatedRoots.current.add(root);

      if (hasRevealed.current) {
        revealInPlace(content);
      } else {
        // eslint-disable-next-line @silverhand/fp/no-mutation
        hasRevealed.current = true;
        revealChunks(content);
      }
    };

    animateNewRoot();

    if (typeof MutationObserver === 'undefined') {
      return;
    }

    const observer = new MutationObserver(animateNewRoot);
    observer.observe(content, { childList: true });

    return () => {
      observer.disconnect();
    };
  }, [contentRef]);

  useLayoutEffect(() => {
    if (previousPathname.current === pathname) {
      return;
    }

    // eslint-disable-next-line @silverhand/fp/no-mutation
    previousPathname.current = pathname;
    // POP covers the browser's Forward button too; only a lower history index means Back.
    const historyIndex = getHistoryIndex();
    // eslint-disable-next-line @silverhand/fp/no-mutation
    isBack.current =
      (navigationType !== NavigationType.Pop && isBackStepState(state)) ||
      (navigationType === NavigationType.Pop &&
        (historyIndex === undefined ||
          previousHistoryIndex.current === undefined ||
          historyIndex < previousHistoryIndex.current));
    // eslint-disable-next-line @silverhand/fp/no-mutation
    previousHistoryIndex.current = historyIndex;
    // eslint-disable-next-line @silverhand/fp/no-mutation
    hasRevealed.current = true;

    const content = contentRef.current;

    if (!content) {
      return;
    }

    const root = content.firstElementChild;
    if (root) {
      animatedRoots.current.add(root);
    }
    slideInPage(content, isBack.current);
  }, [contentRef, navigationType, pathname, state]);
};

export default useStepTransition;
